"use client";
import { Box } from "@mui/material";
import SurveyQuestionBoxes from "./survey-question-boxes";
import { LibraryManagementDialog } from "@/features/library";
import SurveyInfomationForm from "./survey-information";
import { useForm, UseFormHandleSubmit, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	CreateSurveyFormFields,
	SurveyFormActions,
	SurveyFormSchema,
} from "./survey-form.schema";
import React, { memo, useEffect, useImperativeHandle } from "react";
import { Form } from "@onair/minimal-ui/hook-form";
import { cn } from "@onair/utils/cn";

export interface SurveyFormProps {
	action?: SurveyFormActions;
	initialValues: Omit<CreateSurveyFormFields, "action"> | undefined;
	className?: string;
	watchForm?: (formData: Omit<CreateSurveyFormFields, "action">) => void;
	onSubmitForm?: () => void;
}
export interface SurveyFormRef {
	submit: (
		formAction: SurveyFormActions,
	) => UseFormHandleSubmit<CreateSurveyFormFields>;
}
const initSurveyForm: CreateSurveyFormFields = {
	title: "",
	banner: "",
	description: "",
	fields: [],
	action: "Create",
};

const SurveyForm = React.forwardRef<SurveyFormRef, SurveyFormProps>(
	({ className, initialValues, watchForm, action = "Create" }, ref) => {
		const methods = useForm<CreateSurveyFormFields>({
			mode: "onChange",
			defaultValues: {
				...initSurveyForm,
			},
			resolver: zodResolver(SurveyFormSchema),
		});
		const { handleSubmit, setValue, watch, control } = methods;

		const watchAllFields = watch();

		useImperativeHandle(ref, () => {
			return {
				submit: (formAction) => {
					setValue("action", formAction);
					return handleSubmit;
				},
			};
		}, []);

		useEffect(() => {
			watchForm?.(watchAllFields);
		}, [JSON.stringify(watchAllFields)]);

		useEffect(() => {
			const initFormData = initialValues
				? {
						banner: initialValues.banner,
						description: initialValues.description || "",
						fields: initialValues.fields || [],
						title: initialValues.title,
						action: action,
					}
				: initSurveyForm;

			Object.entries(initFormData).forEach(([key, value]) => {
				setValue(key as keyof CreateSurveyFormFields, value);
			});
		}, [initialValues, action]);

		return (
			<Box component="div" className={cn("survey-form", className)}>
				<Form methods={methods}>
					<SurveyInfomationForm methods={methods} />
					<div className="h-6"></div>
					<SurveyQuestionBoxes methods={methods} />
				</Form>
				<LibraryManagementDialog />
			</Box>
		);
	},
);
export default memo(SurveyForm);
